import React, { useState, useEffect, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import VHCard from "../../components/VHCard/VHCard";
import { cardData } from "../../data/cardData";
import {
  useRevealOnScroll,
  useInViewClass,
} from "../../hooks/useRevealOnScroll";

const AUTO_INTERVAL_MS = 14000;

const Overview = () => {
  const { t, i18n } = useTranslation();
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [autoPlay] = useState(true);
  const [paused, setPaused] = useState(false);
  const [progressEpoch, setProgressEpoch] = useState(0);

  const introRef = useRevealOnScroll({
    threshold: 0.06,
    rootMargin: "0px 0px -4% 0px",
  });
  const cardsHolderRef = useInViewClass("vh-hero__deck--visible", {
    /* Any intersection = visible; avoids missing the class after long scroll away/up */
    threshold: 0,
    rootMargin: "0px",
  });

  const focusTab = useCallback((index) => {
    const id = `vh-hero-tab-${cardData[index]?.id}`;
    const el =
      typeof document !== "undefined" ? document.getElementById(id) : null;
    el?.focus({ preventScroll: true });
  }, []);

  const handleDotKeyDown = useCallback(
    (e, index) => {
      const n = cardData.length;
      const rtl = i18n.dir() === "rtl";
      let next = index;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        next = rtl ? (index - 1 + n) % n : (index + 1) % n;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        next = rtl ? (index + 1) % n : (index - 1 + n) % n;
      } else if (e.key === "Home") {
        e.preventDefault();
        next = 0;
      } else if (e.key === "End") {
        e.preventDefault();
        next = n - 1;
      } else {
        return;
      }
      setActiveCardIndex(next);
      focusTab(next);
    },
    [focusTab, i18n]
  );

  const handlePrevCard = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveCardIndex((i) => (i === 0 ? cardData.length - 1 : i - 1));
  }, []);

  const handleNextCard = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveCardIndex((i) => (i === cardData.length - 1 ? 0 : i + 1));
  }, []);

  const handleCardSelect = useCallback((index) => {
    setActiveCardIndex(index);
  }, []);

  const togglePause = useCallback(() => {
    setPaused((p) => !p);
  }, []);

  useEffect(() => {
    setProgressEpoch((e) => e + 1);
  }, [activeCardIndex]);

  useEffect(() => {
    const ae = document.activeElement;
    if (!ae?.id?.startsWith?.("vh-hero-tab-")) return;
    focusTab(activeCardIndex);
  }, [activeCardIndex, focusTab]);

  useEffect(() => {
    if (!autoPlay || paused) return undefined;
    const interval = setInterval(() => {
      setActiveCardIndex((prev) =>
        prev === cardData.length - 1 ? 0 : prev + 1
      );
    }, AUTO_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoPlay, paused]);

  const showAutoplayProgress = autoPlay && !paused;

  return (
    <section
      dir={i18n.dir()}
      className={`section--head-home section--en vh-hero${paused ? " vh-hero--paused" : ""}`}
      aria-labelledby="vh-hero-heading"
    >
      <div className="container-n vh-hero__layout">
        <header
          ref={introRef}
          className="vh-hero__intro reveal reveal--fade-only"
        >
          <div className="vh-hero__eyebrow-row">
            <span className="vh-hero__eyebrow-accent" aria-hidden="true" />
            <p className="vh-hero__eyebrow">{t("hero.eyebrow")}</p>
          </div>
          <h1 id="vh-hero-heading" className="vh-hero__title title-h1">
            {t("hero.title")}
          </h1>
          <p className="vh-hero__lede description-n">{t("hero.lede")}</p>
          <div className="vh-hero__cta-row">
            <a
              href="#project-assessment"
              className="vh-hero__cta vh-hero__cta--primary"
            >
              <span className="vh-hero__cta__label">{t("hero.ctaLabel")}</span>
            </a>
          </div>
        </header>

        <div className="vh-hero__deck">
          <div
            ref={cardsHolderRef}
            className="section__cards-holder vh-hero__cards-holder"
          >
            <div className="vh-hero__deck-head">
              <div className="vh-hero__deck-head-top">
                <p
                  className="vh-hero__deck-label description-n"
                  id="vh-hero-carousel-label"
                >
                  {t("hero.deckLabel")}
                </p>
                <div
                  className="vh-hero__rail"
                  role="tablist"
                  aria-label={t("hero.tablistAria")}
                  aria-describedby="vh-hero-carousel-label"
                >
                  {cardData.map((card, index) => (
                    <button
                      key={card.id}
                      type="button"
                      role="tab"
                      id={`vh-hero-tab-${card.id}`}
                      tabIndex={index === activeCardIndex ? 0 : -1}
                      aria-selected={index === activeCardIndex}
                      aria-controls={`vh-hero-panel-${card.id}`}
                      className={`vh-hero__dot${
                        index === activeCardIndex ? " vh-hero__dot--active" : ""
                      }`}
                      onClick={() => handleCardSelect(index)}
                      onKeyDown={(e) => handleDotKeyDown(e, index)}
                    >
                      <span className="vh-hero__dot-ring" aria-hidden="true" />
                      <span className="vh-hero__dot-fill" aria-hidden="true" />
                      <span className="vh-hero__dot-sr">
                        {t(`heroCards.${card.id}.name`)}
                      </span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={`vh-hero__pause description-n${
                    paused ? " vh-hero__pause--paused" : ""
                  }`}
                  onClick={togglePause}
                  aria-pressed={paused}
                  aria-label={
                    paused ? t("hero.resumeAria") : t("hero.pauseAria")
                  }
                >
                  {paused ? t("hero.resume") : t("hero.pause")}
                </button>
              </div>
              <div
                className="vh-hero__progress-wrap"
                aria-hidden="true"
                data-running={showAutoplayProgress ? "true" : "false"}
              >
                {showAutoplayProgress ? (
                  <div
                    key={progressEpoch}
                    className="vh-hero__progress-bar"
                    style={{
                      animationDuration: `${AUTO_INTERVAL_MS}ms`,
                    }}
                  />
                ) : null}
              </div>
            </div>

            <div
              className="section__cards show vh-hero__cards vh-hero__cards--overview"
              data-id="1"
              data-count="0"
              data-region="default"
              style={{ display: "flex" }}
              role="tabpanel"
              id={`vh-hero-panel-${cardData[activeCardIndex]?.id}`}
              aria-labelledby={`vh-hero-tab-${cardData[activeCardIndex]?.id}`}
            >
              {cardData.map((card, index) => (
                <VHCard
                  key={card.id}
                  data={card}
                  index={index}
                  isActive={index === activeCardIndex}
                  onCardSelect={handleCardSelect}
                  handlePrevCard={handlePrevCard}
                  handleNextCard={handleNextCard}
                  hideCoverImage
                  overviewDeck
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(Overview);
